import './calendar.scss'

import React, { type FC, memo, useCallback, useState } from 'react'
import { useParams } from 'react-router'

import { Tooltip } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'

import { useScreenWidth } from 'app/hooks/useScreenWidth'

import { ReactComponent as PlusCircle } from 'assets/icons/plus-circle.svg'

import { Button } from 'common/components/Button/Button'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import {
  BUTTON_MODIFIER,
  BUTTON_PROPORTION,
  BUTTON_SEVERITY,
} from 'common/constants/buttonConstants'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { useApiResponse } from 'common/hooks/useApiResponse'
import type { ApiResponse } from 'common/interfaces/IRequestResponse'

import { getRoomAliasId, getRoomName } from 'features/Home/Promotion/PromotionDetails/Rooms/Rooms'
import { useDeleteSlotBookingMutation } from 'features/Home/Promotion/state/promotionsApi'
import { removeRoomFromErrorSlots } from 'features/Home/Promotion/state/promotionsSlice'
import type {
  PromotionSlot,
  PromotionSlotRoom,
} from 'features/Home/Promotion/types/promotion.types'
import SlotDetails, { SlotDetailsList } from './SlotDetails'
import type { CalendarProps } from './calendar.types'
import { RoomAvailability } from 'features/Home/Promotion/PromotionDetails/Rooms/RoomAvailability/RoomAvailability'
import Popup from 'common/components/Popup'
import { groupSlots } from 'features/Booking/components/BookingSlots/booking.utils'

const CELL_HEIGHT = 80

function calculateSlotFactor(slot: PromotionSlot) {
  const start_time = slot.start_time.split(':')
  const end_time = slot.end_time.split(':')
  const start = dayjs()
    .hour(Number(start_time[0]))
    .minute(Number(start_time[1]))
    .second(Number(start_time[2]))
  const end = dayjs()
    .hour(Number(end_time[0]))
    .minute(Number(end_time[1]))
    .second(Number(end_time[2]))
  return end.diff(start, 'minute') / 60
}

function formatSlotHour(time: string) {
  return dayjs(time, 'h:mm:ss').format('h:mm A')
}

function formatSlotDetailsDate(date: string, slotTime: string) {
  return `${dayjs(date).format('MMMM D')} ${slotTime}`
}

export const Calendar: FC<CalendarProps> = memo(({ isLoading, promotion, bookingsData }) => {
  const [bookingDate, setBookingDate] = useState(null)

  const dispatch = useAppDispatch()

  const { breakpoints } = useScreenWidth()
  const { processApiResponse } = useApiResponse()
  const { bookingId, siteId } = useParams()

  const [deleteBookingSlot, { isLoading: isDeletingBookingSlot }] = useDeleteSlotBookingMutation()
  const { promotionErrorSlots } = useAppSelector((state) => state.promotionReducer)

  const loading = isLoading || isDeletingBookingSlot

  const formatHeaderDate = (date: string): string => {
    const format = 'ddd[\n]D'
    return dayjs(date).format(format)
  }

  function getRoomsToDelete(
    date: string,
    slotId: number,
    room: PromotionSlotRoom,
    isGrouped: boolean,
  ) {
    if (!isGrouped) {
      return [room]
    }
    try {
      const bookingDate = bookingsData.bookings.find((booking) => booking.date === date)!

      const slotGroup = bookingDate.slots.find((slot) => slot.id === slotId).group_time!

      const slotGroups = groupSlots(bookingDate.slots)

      const entireGroup = slotGroups[slotGroup]
      const roomsToDelete = entireGroup.flatMap((slot) =>
        slot.rooms
          .filter((r) => r.name === room.name)
          .map((r) => ({
            ...r,
            start_time: slot.start_time,
            end_time: slot.end_time,
          })),
      )

      return roomsToDelete
    } catch (error) {
      return []
    }
  }

  const handleDeleteBooking = useCallback(
    async (roomIds: number[]) => {
      const response = await deleteBookingSlot({ bookingId, siteId, ids: roomIds })
      processApiResponse(response as ApiResponse, {
        success: 'Booking removed!',
        error: 'Failed to remove booking',
      })
    },
    [bookingId, siteId],
  )

  const handleDismissErrorRoom = (date: string, slotId: number, roomId: number) => {
    dispatch(
      removeRoomFromErrorSlots({
        date,
        slotId,
        roomId,
      }),
    )
  }

  const handleRemoveBooking = (params: {
    hasError: boolean
    date: string
    slotId: number
    room: PromotionSlotRoom
    slot_usage_code: number
  }) => {
    const { hasError, date, slotId, room, slot_usage_code } = params

    if (hasError) {
      return handleDismissErrorRoom(date, slotId, room.id)
    }

    const isGrouped = slot_usage_code === 1

    const roomsToDelete = getRoomsToDelete(date, slotId, room, isGrouped)

    const roomIds = roomsToDelete.map((r) => r.id)
    const hasAppointment = roomsToDelete.some((r) => r.has_appointment)

    if (isGrouped || hasAppointment) {
      return Popup.showInfo(
        {
          title: isGrouped ? 'Remove Slot Group' : 'Remove Slot Interval',
          message: isGrouped
            ? 'The current slot is part of a group. Do you want to remove the entire group?'
            : 'You have appointments set for this room and time interval. Do you want to remove this time slot?',
          mainButtonText: 'Confirm',
        },
        () => handleDeleteBooking(roomIds),
      )
    }
    handleDeleteBooking(roomIds)
  }

  const isBookAvailable = (date: string): string | undefined => {
    const currentDate = dayjs().startOf('day')
    const selectedDate = dayjs(date).startOf('day')

    if (selectedDate.isBefore(currentDate)) {
      return "You can't book slots in the past!"
    }

    if (selectedDate.isAfter(dayjs(promotion?.end_date).startOf('day'))) {
      return 'Booking is not available after or on the package end date!'
    }

    const dayOfWeek = selectedDate.day()
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

    const isAvailable = bookingsData.convention.rooms.some((room) => {
      const availableSlots = isWeekend
        ? room.weekend_slots
        : room.weekday_slots + room.weekend_slots
      return availableSlots > 0
    })

    return isAvailable ? undefined : "You've reached maximum slots for this week!"
  }

  return (
    <>
      {!!bookingDate && (
        <RoomAvailability
          open={!!bookingDate}
          handleCancel={() => setBookingDate(null)}
          date={bookingDate}
          conventionRooms={bookingsData?.convention?.rooms}
          isFlexibleRooms={!!promotion?.has_flexible_rooms}
        />
      )}
      <div className='promotion-calendar'>
        <SpinnerOverlay isLoading={loading} />
        <div className='main-grid'>
          {bookingsData?.bookings?.map((booking) => {
            const bookingDate = booking.date
            const formattedDate = formatHeaderDate(bookingDate)
            const isDisabledBooking = isBookAvailable(bookingDate)

            const errorsSlots = promotionErrorSlots[bookingDate] ?? []

            const combinedSlots = booking.slots.map((sl) => {
              const findSlot = errorsSlots.find((errorSl) => errorSl.id === sl.id)
              const findSlotsRooms = findSlot?.rooms ?? []
              return {
                ...sl,
                rooms: [...sl.rooms, ...findSlotsRooms],
              }
            })

            return (
              <div key={booking.date} className='calendar-col'>
                <Tooltip title={isDisabledBooking}>
                  <div className='header-cell'>
                    <button
                      className='book-button'
                      onClick={() => setBookingDate(booking.date)}
                      disabled={!!isDisabledBooking}>
                      {!isDisabledBooking && <PlusCircle width={18} height={18} />}
                      {!isDisabledBooking ? 'Book' : 'N/A'}
                    </button>
                    <p className='header-date-label'>{formattedDate}</p>
                  </div>
                </Tooltip>
                <div key={booking.date} className='body-col'>
                  {combinedSlots.map((slot) => {
                    const factor = calculateSlotFactor(slot)
                    let showMoreValue = 0
                    let showMoreAdded = false

                    const isNotClickable = isDisabledBooking || !!slot.rooms.length
                    return (
                      <div
                        onClick={isNotClickable ? undefined : () => setBookingDate(booking.date)}
                        style={{ height: factor * CELL_HEIGHT }}
                        key={slot.id}
                        className={classNames('body-cell', {
                          clickable: !isNotClickable,
                        })}>
                        {!slot.rooms.length && (
                          <span className='empty-slot'>
                            {formatSlotHour(slot.start_time)} - {formatSlotHour(slot.end_time)}
                          </span>
                        )}
                        {slot.rooms.map((room, roomInd, roomsArr) => {
                          const hideExtraSmall = (breakpoints.xs || breakpoints.sm) && roomInd > 0
                          const hideExtraMedium = (breakpoints.md || breakpoints.lg) && roomInd > 0
                          const hideExtraLarge = (breakpoints.lg || breakpoints.xl) && roomInd > 1
                          const hideExtraXLarge = breakpoints.xxl && roomInd >= 2

                          const hides = [
                            hideExtraSmall,
                            hideExtraMedium,
                            hideExtraLarge,
                            hideExtraXLarge,
                          ]

                          const prevAdded = showMoreAdded

                          const isHide = hides.includes(true)

                          if (isHide && !showMoreValue && !showMoreAdded) {
                            showMoreValue = roomsArr.length - roomInd
                            showMoreAdded = true
                          }

                          const slotTime = `${formatSlotHour(slot.start_time)} - ${formatSlotHour(
                            slot.end_time,
                          )}`

                          const hasRoomError = room?.status === 'error'
                          const roomAliasId = getRoomAliasId(
                            room.name,
                            bookingsData.convention.rooms,
                            room.room_alias_id,
                          )

                          return (
                            <React.Fragment key={room.id}>
                              <Tooltip
                                align={{ offset: [0, 10] }}
                                overlayClassName='slot-details'
                                trigger='click'
                                title={
                                  <SlotDetails
                                    hasError={hasRoomError}
                                    isDeleteDisabled={loading}
                                    isGrouped={booking.slot_usage_code === 1}
                                    bookingId={bookingId}
                                    siteId={siteId}
                                    slotId={slot.id}
                                    roomsToDelete={getRoomsToDelete(
                                      bookingDate,
                                      slot.id,
                                      room,
                                      booking.slot_usage_code === 1,
                                    )}
                                    handleDeleteBooking={() =>
                                      handleRemoveBooking({
                                        hasError: hasRoomError,
                                        date: bookingDate,
                                        slotId: slot.id,
                                        room,
                                        slot_usage_code: booking.slot_usage_code,
                                      })
                                    }
                                    room={{
                                      ...room,
                                      date: booking.date,
                                      timeSlot: formatSlotDetailsDate(booking.date, slotTime),
                                      roomAliasId,
                                    }}
                                  />
                                }
                                destroyTooltipOnHide>
                                <div
                                  data-error={hasRoomError}
                                  className={classNames('room', {
                                    hide: isHide,
                                  })}>
                                  <span className='base-label'>
                                    {formatSlotHour(slot.start_time)} -{' '}
                                    {formatSlotHour(slot.end_time)}
                                  </span>
                                  <span className='room-name'>{getRoomName(room.name, true)}</span>
                                  {hasRoomError && (
                                    <span className='base-label'>
                                      {'UNABLE TO RESERVE,\nPLEASE TRY ANOTHER SLOT'}
                                    </span>
                                  )}
                                  {hasRoomError && (
                                    <Button
                                      onClick={() =>
                                        handleDismissErrorRoom(bookingDate, slot.id, room.id)
                                      }
                                      proportion={BUTTON_PROPORTION.SMALL}
                                      modifier={BUTTON_MODIFIER.DEFAULT}
                                      severity={BUTTON_SEVERITY.DANGER_OUTLINE}>
                                      Dismiss
                                    </Button>
                                  )}
                                </div>
                              </Tooltip>
                              {showMoreAdded && !prevAdded && (
                                <Tooltip
                                  destroyTooltipOnHide
                                  align={{ offset: [0, 10] }}
                                  overlayClassName='slot-details'
                                  trigger='click'
                                  title={
                                    <SlotDetailsList
                                      isDeleteDisabled={loading}
                                      isGrouped={booking.slot_usage_code === 1}
                                      bookingId={bookingId}
                                      siteId={siteId}
                                      slotId={slot.id}
                                      roomsToDelete={getRoomsToDelete(
                                        bookingDate,
                                        slot.id,
                                        room,
                                        booking.slot_usage_code === 1,
                                      )}
                                      handleDeleteBooking={(hasErr) =>
                                        handleRemoveBooking({
                                          hasError: hasErr,
                                          date: bookingDate,
                                          slotId: slot.id,
                                          room,
                                          slot_usage_code: booking.slot_usage_code,
                                        })
                                      }
                                      rooms={slot.rooms.map((slotRoom) => ({
                                        ...slotRoom,
                                        date: booking.date,
                                        timeSlot: formatSlotDetailsDate(booking.date, slotTime),
                                        roomAliasId: getRoomAliasId(
                                          slotRoom.name,
                                          bookingsData.convention.rooms,
                                          slotRoom.room_alias_id,
                                        ),
                                      }))}
                                    />
                                  }>
                                  <p className='more'>+{showMoreValue} Show More</p>
                                </Tooltip>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
})
